import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, createMembershipInvite } from "~/lib/api";
import { memberAppUrl } from "~/lib/member-app";
import { queryKeys } from "~/lib/query-client";
import type { MembershipInviteCreateResponse } from "~/types/users-admin";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function translateInviteError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "invite.errors.alreadyPending";
    if (error.status === 422) return "invite.errors.invalidEmail";
    return "common.somethingWentWrong";
  }
  return "invite.errors.network";
}

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Create an e-mail-bound invite and expose its one-time raw link for copying. */
export function InviteDialog({ open, onClose }: InviteDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<MembershipInviteCreateResponse | null>(
    null,
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: createMembershipInvite,
    onSuccess: async (created) => {
      setResult(created);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.membershipInvites,
      });
    },
    onError: (error) => setErrorKey(translateInviteError(error)),
  });

  const inviteUrl = result ? memberAppUrl(result.invite_path) : null;

  const close = () => {
    if (create.isPending) return;
    setEmail("");
    setResult(null);
    setErrorKey(null);
    setCopied(false);
    create.reset();
    onClose();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = email.trim();
    if (!EMAIL_PATTERN.test(normalized)) {
      setErrorKey("invite.errors.invalidEmail");
      return;
    }
    setErrorKey(null);
    setCopied(false);
    create.mutate(normalized);
  };

  const copy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setErrorKey(null);
    } catch {
      setErrorKey("invite.errors.copyFailed");
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle>{t("invite.title")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <DialogContentText>{t("invite.subtitle")}</DialogContentText>

          {result && inviteUrl ? (
            <>
              <Alert severity="success" data-testid="invite.created">
                {t("invite.created", { email: result.email })}
              </Alert>
              <TextField
                label={t("invite.link")}
                value={inviteUrl}
                fullWidth
                slotProps={{
                  htmlInput: {
                    readOnly: true,
                    "data-testid": "invite.link",
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          type="button"
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => void copy()}
                          data-testid="invite.copy"
                        >
                          {t(copied ? "invite.copied" : "invite.copy")}
                        </Button>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <DialogContentText>{t("invite.linkWarning")}</DialogContentText>
            </>
          ) : (
            <TextField
              label={t("invite.email")}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
              fullWidth
              slotProps={{
                htmlInput: {
                  "data-testid": "invite.email",
                  inputMode: "email",
                  maxLength: 320,
                },
              }}
            />
          )}

          {errorKey ? (
            <Alert severity="error" data-testid="invite.error">
              {t(errorKey)}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={close}
            disabled={create.isPending}
            data-testid="invite.close"
          >
            {t(result ? "common.close" : "common.cancel")}
          </Button>
          {!result ? (
            <Button
              type="submit"
              variant="contained"
              disabled={create.isPending}
              data-testid="invite.create"
            >
              {t("invite.create")}
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
